import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import NavLink from './common/NavLink';
import RequireVip from './RequireVip'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Box } from '@mui/material';
import { useCompetitions } from './CompetitionContext';
import { slugify } from '../utils/slugify'; 


// Funkcja do obliczania koloru gradientu w zależności od difficulty_diff
const getDifficultyGradientColor = (value, maxAbsDiff) => {
  if (value === 0) return 'rgba(255, 255, 255, 0.2)'; // Neutralny szary dla 0
  const maxAbs = maxAbsDiff; // Maksymalna wartość bezwzględna dla skalowania
  const scaledValue = Math.abs(value) / maxAbs; // Skalowanie na podstawie abs

  const grayBase = 255;
  let r, g, b, alpha;

  if (value > 0) {
    // Zielony gradient dla łatwiejszego terminarza
    r = Math.round(grayBase * (1 - scaledValue));
    g = Math.round(grayBase * (1 - scaledValue) + 255 * scaledValue);
    b = Math.round(grayBase * (1 - scaledValue));
  } else {
    // Czerwony gradient dla trudniejszego terminarza
    r = Math.round(grayBase * (1 - scaledValue) + 255 * scaledValue);
    g = Math.round(grayBase * (1 - scaledValue));
    b = Math.round(grayBase * (1 - scaledValue));
  }

  alpha = 0.2 + scaledValue * 0.6; // Alfa od 0.2 do 0.8
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const FixtureDifficulty = () => {
  const { competitionId } = useParams();
  const { competitions, loading } = useCompetitions();
  const [data, setData] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [maxAbsDiff, setMaxAbsDiff] = useState(50); // Domyślna wartość, jeśli brak danych
  const [leagueName, setLeagueName] = useState(''); // Dodajemy zmienną dla nazwy ligi
  const [selectedComp, setSelectedComp] = useState(null); // ← Dodany stan dla selectedComp


  useEffect(() => {
    if (loading || !competitionId) return;

    const selectedComp = Object.values(competitions)
      .flat()
      .find(comp => comp.competition_id === parseInt(competitionId));

    if (!selectedComp) {
      setLeagueName('Liga');
      return;
    }
	setSelectedComp(selectedComp);

    // Formatowanie nazwy ligi i grupy jak w PointsThreshold
    const displayName =
      selectedComp.group && selectedComp.group !== 'NULL' && selectedComp.group.trim() !== ''
        ? `${selectedComp.name} (${selectedComp.group.charAt(0).toUpperCase() + selectedComp.group.slice(1)})`
        : selectedComp.name;
    setLeagueName(displayName);

    setLoadingData(true);
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/fixture_difficulty/${competitionId}`)
      .then((response) => {
        const responseData = response.data;
        setData(responseData);
        // Oblicz maksymalną wartość bezwzględną difficulty_diff
        const maxDiff = Math.max(...responseData.map((team) => Math.abs(team.difficulty_diff)), 0);
        setMaxAbsDiff(maxDiff || 50); // Użyj 50 jako fallback, jeśli maxDiff = 0
        setLoadingData(false);
      })
      .catch((error) => {
        console.error('Błąd podczas pobierania danych trudności terminarza:', error);
        setLoadingData(false);
      });
  }, [competitionId, competitions, loading]);

  if (loading || loadingData) {
    return <Typography variant="h6">Ładowanie danych...</Typography>;
  }

  if (data.length === 0) {
    return <Typography variant="h6">Brak danych o trudności terminarza dla tej ligi.</Typography>;
  }

  return (
	<RequireVip variant="vip-teaser" requirement={selectedComp.stats_requirement}>
		<Box sx={{ padding: { xs: 2, sm: 4 }, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>		
		<Typography variant="h5" sx={{ textAlign: { xs: 'center', sm: 'center' }, padding: '10px 10px', fontSize: { xs: '1.0rem', sm: '1.2rem' } }}>
			Łatwość Terminarza - {leagueName}
		</Typography>
		  
			  <TableContainer component={Paper} sx={{ marginBottom: 4, borderRadius: '12px', width: 'auto', maxWidth: 'fit-content' }}>
				<Table sx={{ width: 'auto' }}>
				  <TableHead>
					<TableRow>
					  <TableCell sx={{ minWidth: 20, padding: '6px' }}>#</TableCell>
					  <TableCell sx={{ minWidth: 50, padding: '6px' }}>Drużyna</TableCell>
					  <TableCell align="center" sx={{ padding: '6px' }}>Śr. Elo przeciwnika</TableCell>
					  <TableCell align="center" sx={{ padding: '6px' }}>Łatwość terminarza</TableCell>
					</TableRow>
				  </TableHead>
				  <TableBody>
					{data.map((team, index) => (
					  <TableRow key={team.team_id}>
						<TableCell sx={{ padding: '6px' }}>{index + 1}</TableCell>
						<TableCell component={NavLink} to={`/team/${team.team_id}/${slugify(team.team_name)}`} sx={{ padding: '6px' }}>					
							{team.team_name}										
						</TableCell>
						<TableCell align="center" sx={{ fontWeight: 'bold', padding: '6px' }}>{team.avg_opponent_elo.toFixed(1)}</TableCell>
						<TableCell
						  align="center"
						  sx={{
							backgroundColor: getDifficultyGradientColor(team.difficulty_diff, maxAbsDiff),
							color: team.difficulty_diff < -maxAbsDiff / 2 ? 'white' : 'black', // Biały poniżej połowy negatywnej wartości
							fontWeight: 'bold', // Pogrubienie
							padding: '6px', // Zmniejszony padding
							textShadow: '0.5px 0.5px 1px rgba(0, 0, 0, 0.3)', // Cień dla kontrastu
							transition: 'background-color 0.3s',
							'&:hover': {
							  backgroundColor: getDifficultyGradientColor(team.difficulty_diff + (team.difficulty_diff > 0 ? 10 : -10), maxAbsDiff),
							  transform: 'scale(1.05)',
							},
						  }}
						>
						  {team.difficulty_diff.toFixed(1)}
						</TableCell>
					  </TableRow>
					))}
				  </TableBody>
				</Table>
			  </TableContainer>
		  <Typography variant="body2" sx={{ textAlign: 'center', maxWidth: 600, color: 'text.secondary' }}>
			Średnie Elo przeciwników w meczach pozostałych do rozegrania. Łatwość terminarza oznacza o ile łatwiejszych (+) lub trudniejszych (-) przeciwników ma drużyna w stosunku do średniej w lidze. Gospodarz meczów ma +35 pkt. Elo, gość -35 pkt. Elo.
		  </Typography>
		</Box>
	</RequireVip>

  );
};

export default FixtureDifficulty;